import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  InputAdornment,
  TextField,
  Tooltip,
} from "@mui/material";
import { DatePicker, DateValidationError } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import React, { Dispatch, SetStateAction, useEffect, useState } from "react";
import { padZerosLeft } from "../../../commons/helpers/StringHelper";
import {
  DATE_ENTREE_TOOLTIP,
  DATE_SORTIE_TOOLTIP,
} from "../../../constante/headers/headerConstants";
import { PTR_LENGTH } from "../../../constante/produits";
import { Planogramme } from "../../../models/Planogramme";
import { ProduitQuantite } from "../../../models/ProduitQuantite";
import { RequestToSend } from "../../../models/RequestToSend";
import { addRequestToSend } from "../../../redux/features/appStateSlice";
import {
  useDeletePlanogrammeMutation,
  useGetEnsembleQuery,
  useGetMagasinsQuery,
  useGetPlanogrammeProduitsQuery,
  useUpdatePlanogrammeMutation,
  useUpdatePlanogrammeProduitsMutation,
} from "../../../redux/features/cache";
import { useAppDispatch, useAppSelector } from "../../../redux/store";
import { getModalStyle } from "../../modalStyle";
import GenericModal from "../modals/GenericModal";
import PlanoProduitsModal from "../modals/PlanoProduitsModal";
import PTRModal from "../modals/PTRModal";

export interface IFormEditMagasinProduit {
  index: number;
  data: Planogramme;
  setUnsaved: Dispatch<SetStateAction<boolean>>;
  listChecked: Planogramme[];
}

function FormEditMagasinProduit({
  listChecked,
  data,
  setUnsaved,
}: IFormEditMagasinProduit) {
  const [open, setOpen] = useState(false);
  const [showModalPlanoProduits, setShowModalPlanoProduits] =
    useState<boolean>(false);
  const dispatch = useAppDispatch();
  const [deletePlanogramme] = useDeletePlanogrammeMutation();
  const [updatePlanogrammeProduits] = useUpdatePlanogrammeProduitsMutation();
  const handleClose = () => {
    setOpen(false);
  };
  const { data: magasins = [] } = useGetMagasinsQuery({
    banniere: "",
    treeView: false,
  });
  const { data: ensemble = [] } = useGetEnsembleQuery(data.prod_num);
  const { data: planogrammeProduits = [] } = useGetPlanogrammeProduitsQuery(
    data.plano_seq
  );
  const [updatePlanogramme] = useUpdatePlanogrammeMutation();
  const [errorDateEntree, setErrorDateEntree] =
    React.useState<DateValidationError | null>(null);
  const [errorDateSortie, setErrorDateSortie] =
    React.useState<DateValidationError | null>(null);
  const [magValue, setMagValue] = useState<string>(data.magasin.mag_nom_aff);
  const [dateEntre, setDateEntre] = useState<any>(
    data?.plano_trsf_dt_entree !== null
      ? dayjs(data?.plano_trsf_dt_entree.replaceAll("-", "/"))
      : null
  );
  const [dateSorti, setDateSorti] = useState<any>(
    data?.plano_trsf_dt_sortie !== null
      ? dayjs(data?.plano_trsf_dt_sortie.replaceAll("-", "/"))
      : null
  );
  const [value, setValue] = useState<number | null>(
    data?.plano_prod_num_remp && data?.plano_prod_num_remp !== 0
      ? data?.plano_prod_num_remp
      : null
  );
  const [plateau, setPlateau] = useState<number>(data.plano_plateau);
  const [remplacer, setRemplacer] = useState<boolean>(data.plano_remplacer);
  const [isDirty, setIsDirty] = useState<boolean>(false);
  const [planoQuantite, setPlanoQuantite] = useState<number>(data.plano_qte);
  const [ensembleValues, setEnsembleValues] = useState<ProduitQuantite[]>(
    planogrammeProduits.map((e) => {
      return {
        prod_num: e.prod_num,
        plano_qte: e.plano_qte,
      };
    })
  );

  const prod_plano = useAppSelector((state) => state.appState.prod_plano);

  useEffect(() => {
    setUnsaved(isDirty);
  }, [isDirty, setUnsaved]);

  useEffect(() => {
    setEnsembleValues(
      planogrammeProduits.map((e) => {
        return {
          prod_num: e.prod_num,
          plano_qte: e.plano_qte,
        };
      })
    );
  }, [planogrammeProduits]);

  useEffect(() => {
    if (data) {
      setMagValue(data.magasin.mag_nom_aff);
      setDateEntre(
        data?.plano_trsf_dt_entree !== null
          ? dayjs(data?.plano_trsf_dt_entree.replaceAll("-", "/"))
          : null
      );
      setDateSorti(
        data?.plano_trsf_dt_sortie !== null
          ? dayjs(data?.plano_trsf_dt_sortie.replaceAll("-", "/"))
          : null
      );
      setValue(
        data?.plano_prod_num_remp && data?.plano_prod_num_remp !== 0
          ? data?.plano_prod_num_remp
          : null
      );
      setIsDirty(false);
      setPlanoQuantite(data.plano_qte);
      setPlateau(data.plano_plateau);
    }
  }, [data, magasins]);

  const submit = async () => {
    let datas = {
      mag_num: data?.mag_num,
      plano_seq: data?.plano_seq,
      prod_num: data?.prod_num,
      plans_seq: data?.plans_seq,
      plano_dt_deb: data?.plano_dt_deb,
      plano_dt_fin: data?.plano_dt_fin,
      plano_qte: planoQuantite,
      plano_trsf_dt_entree:
        dateEntre !== null
          ? typeof dateEntre == "string"
            ? dateEntre.replaceAll("-", "/")
            : dateEntre.format("YYYY/MM/DD")
          : null,
      plano_trsf_dt_sortie:
        dateSorti !== null
          ? typeof dateSorti == "string"
            ? dateSorti.replaceAll("-", "/")
            : dateSorti.format("YYYY/MM/DD")
          : null,
      plano_prod_num_remp: value,
      statut_aff: data?.statut_aff,
      plano_plateau: plateau,
      plano_remplacer: remplacer
    } as Planogramme;
    
    dispatch(
      addRequestToSend(
        new RequestToSend(
          `Modification planogramme ${data.magasin.mag_nom}`,
          updatePlanogramme,
          (result) => {
            if (ensemble.length > 0)
              dispatch(
                addRequestToSend(
                  new RequestToSend(
                    `Sauvegarde produit/ensemble ${data.magasin.mag_nom}`,
                    updatePlanogrammeProduits,
                    (result) => {
                      if (!result.status) setIsDirty(false);
                    },
                    { planoSeq: data.plano_seq },
                    ensembleValues,
                    ["planogrammeProduits"]
                  )
                )
              );
            else if (!result.status) setIsDirty(false);
          },
          {},
          datas,
          []
        )
      )
    );
  };

  const handleDelete = async () => {
    if (window.confirm("Voulez vous vraiment supprimer le planogramme?")) {
      dispatch(
        addRequestToSend(
          new RequestToSend(
            `Supprimer planogramme ${data.magasin.mag_nom}`,
            deletePlanogramme,
            () => {},
            {},
            { plano_seq: data?.plano_seq },
            []
          )
        )
      );
    }
  };

  const handleCardClick = (ptr: number) => {
    setValue(ptr);
    setOpen(false);
  };

  return (
    <>
      <GenericModal
        open={open}
        handleClose={handleClose}
        title="PTR"
        style={getModalStyle}
      >
        <PTRModal handleCardClick={handleCardClick} />
      </GenericModal>
      <GenericModal
        open={showModalPlanoProduits}
        handleClose={() => setShowModalPlanoProduits(false)}
        title="Produits inclus"
        style={getModalStyle}
      >
        <PlanoProduitsModal
          prodNum={data.prod_num}
          ensembleValues={ensembleValues}
          closeModal={() => setShowModalPlanoProduits(false)}
          update={(values: ProduitQuantite[]) => {
            setEnsembleValues(values);
            setIsDirty(true);
          }}
        />
      </GenericModal>
      <Box
        data-component="form-edit-magasin-produit"
        sx={{ display: "flex" }}
      >
        <Box
          display={"flex"}
          flexDirection={"column"}
        >
          <Box display={"flex"}>
            {data?.mag_num && (
              <TextField
                value={magValue}
                disabled
                sx={{ m: 1, width: 200, flexShrink: 0 }}
              />
            )}

            <TextField
              label="Quantité"
              type="number"
              sx={{ width: "75px", m: 1 }}
              InputLabelProps={{
                shrink: true,
              }}
              value={planoQuantite}
              InputProps={{ inputProps: { min: 1, max: 100 } }}
              onChange={(e) => {
                if (!isDirty) setIsDirty(true);
                if (e.target.value !== "") {
                  setPlanoQuantite(Number(e.target.value));
                }
              }}
              disabled={ensemble.length > 0}
              onClick={(e) => {
                e.stopPropagation();
              }}
            />

            <Tooltip
              placement={"bottom"}
              title={DATE_ENTREE_TOOLTIP}
            >
              <div>
                <DatePicker
                  disabled={listChecked.length > 0 ? true : false}
                  onAccept={(newDate) => {
                    setDateEntre(newDate);
                  }}
                  value={dateEntre}
                  label="Date d'entrée"
                  onChange={(event) => {
                    setIsDirty(true);
                    setDateEntre(event ? event.format("YYYY/MM/DD") : null);
                  }}
                  sx={{ m: 1, width: 200, flexShrink: 0 }}
                  onError={(newError) => setErrorDateEntree(newError)}
                  slotProps={{
                    textField: {
                      helperText:
                        errorDateEntree === "invalidDate"
                          ? "Le format est invalide"
                          : "",
                    },
                    actionBar: {
                      actions: ["clear"],
                    },
                  }}
                  format="DD/MM/YYYY"
                />
              </div>
            </Tooltip>
            <Tooltip
              placement={"bottom"}
              title={DATE_SORTIE_TOOLTIP}
            >
              <div>
                <DatePicker
                  disabled={listChecked.length > 0 ? true : false}
                  onAccept={(newDate) => {
                    setDateSorti(newDate);
                  }}
                  value={dateSorti}
                  label="Date de sortie"
                  onChange={(event) => {
                    setIsDirty(true);
                    setDateSorti(event ? event.format("YYYY/MM/DD") : null);
                  }}
                  sx={{ m: 1, width: 200, flexShrink: 0 }}
                  onError={(newError) => setErrorDateSortie(newError)}
                  slotProps={{
                    textField: {
                      helperText:
                        errorDateSortie === "invalidDate"
                          ? "Le format est invalide"
                          : "",
                    },
                    actionBar: {
                      actions: ["clear"],
                    },
                  }}
                  format="DD/MM/YYYY"
                />
              </div>
            </Tooltip>
            <TextField
              inputProps={{
                type: "number",
              }}
              disabled={listChecked.length > 0 ? true : false}
              type="search"
              label="Prend la place de..."
              value={value ? padZerosLeft(value, PTR_LENGTH) : ""}
              onBlur={() => {
                if (value) setValue(value);
              }}
              onChange={(event) => {
                setIsDirty(true);
                const newValue = event.currentTarget.value;
                const onlyDigitsRegex = /^\d+$/;

                const isOnlyDigits = onlyDigitsRegex.test(newValue);

                setValue(isOnlyDigits ? parseInt(newValue) : null);
              }}
              sx={{
                m: 1,
                width: 200,
                color: "#233044",
                background: "white",
                flexShrink: 0,
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Button
                      onClick={() => setOpen(true)}
                      disabled={listChecked.length > 0}
                    >
                      <SearchIcon />
                    </Button>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              disabled={listChecked.length > 0 ? true : false}
              value={plateau}
              label="Plateau"
              type="number"
              sx={{ m: 1, width: "5rem" }}
              onChange={(event) => {
                setPlateau(Number(event.currentTarget.value));
              }}
            />
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
            }}
          >
            {ensemble.length > 0 && (
              <TextField
                disabled={listChecked.length > 0}
                type="search"
                label="Produits inclus"
                value={
                  ensembleValues
                    .map((e) => padZerosLeft(e.prod_num, PTR_LENGTH))
                    .join(", ") || ""
                }
                onChange={(event) => {
                  setIsDirty(true);
                }}
                sx={{
                  m: 1,
                  color: "#233044",
                  background: "white",
                  flexGrow: 1,
                }}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <Button
                        onClick={() => setShowModalPlanoProduits(true)}
                        disabled={listChecked.length > 0}
                      >
                        <EditIcon />
                      </Button>
                    </InputAdornment>
                  ),
                }}
              />
            )}
            <FormControlLabel
              disabled={listChecked.length > 0}
              checked={remplacer}
              onChange={(_event, checked) => {
                setRemplacer(checked);
                setIsDirty(true);
              }}
              label="Remplacé"
              control={<Checkbox />}
              style={{ marginLeft: "auto" }}
            />
          </Box>
        </Box>
        <Tooltip title="Enregistrer">
          <span
            style={{
              display: "flex",
              alignItems: "center",
              padding: "6px 28px",
              cursor: "pointer",
            }}
            className={`save-wrapper ${
              !prod_plano.canModify ? "custom-disabled" : ""
            }`}
            onClick={submit}
          >
            <span className={`k-icon k-font-icon k-i-save k-icon-xl`}></span>
          </span>
        </Tooltip>
        <Tooltip title="Effacer">
          <span style={{ display: "flex" }}>
            <Button
              onClick={handleDelete}
              color="error"
              disabled={
                !prod_plano.canDelete || listChecked.length > 0 ? true : false
              }
            >
              {" "}
              <DeleteIcon />
            </Button>
          </span>
        </Tooltip>
      </Box>
    </>
  );
}

export default React.memo(FormEditMagasinProduit);
